import React, { useId } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const STAGES = ['Discover', 'Define', 'Develop', 'Deliver'] as const;
type Stage = typeof STAGES[number];

const STAGE_DESCRIPTIONS: Record<Stage, string> = {
  Discover: 'Exploring the problem space',
  Define:   'Framing the problem clearly',
  Develop:  'Generating solutions',
  Deliver:  'Refining and validating',
};

function progressToStage(progress: number): Stage {
  if (progress < 25) return 'Discover';
  if (progress < 50) return 'Define';
  if (progress < 75) return 'Develop';
  return 'Deliver';
}

// Viewbox: 0 0 500 180
// Left diamond:  (0,90)  (125,10) (250,90) (125,170)
// Right diamond: (250,90) (375,10) (500,90) (375,170)
const VW = 500;
const VH = 180;
const MID_X = VW / 2;
const TOP_Y = 10;
const BOT_Y = VH - 10;
const MID_Y = VH / 2;
const Q1_X = VW / 4;       // 125
const Q3_X = (3 * VW) / 4; // 375

const LEFT_DIAMOND = `${0},${MID_Y} ${Q1_X},${TOP_Y} ${MID_X},${MID_Y} ${Q1_X},${BOT_Y}`;
const RIGHT_DIAMOND = `${MID_X},${MID_Y} ${Q3_X},${TOP_Y} ${VW},${MID_Y} ${Q3_X},${BOT_Y}`;

const STAGE_LABEL_X = [VW * 0.125, VW * 0.375, VW * 0.625, VW * 0.875];

export const DoubleDiamondWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state } = useSecondScreenData();
  const clipId = useId().replace(/:/g, '');
  const glowId = useId().replace(/:/g, '');
  const cinematic = config?.cinematic === true;

  const lastKnownRef = React.useRef<import('../../../types/second-screen.types').DoubleDiamondProgress | null>(null);
  const incoming = state?.doubleDiamondProgress ?? null;
  if (incoming !== null) lastKnownRef.current = incoming;
  const dd = lastKnownRef.current;

  const progress = dd?.progress ?? 0;
  const milestone = dd?.milestone ?? null;
  const nextStep = dd?.nextStep ?? null;
  const currentStage = dd ? progressToStage(progress) : null;

  // Clip rect width: progress% of full viewbox width
  const clipWidth = (progress / 100) * VW;

  return (
    <div className="flex flex-col h-full w-full p-3 gap-2 select-none">

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <span
          className="font-semibold tracking-widest uppercase text-white/60"
          style={{ fontSize: cinematic ? '0.8rem' : '0.65rem' }}
        >
          Double Diamond Progress
        </span>
        {currentStage && (
          <span
            className="font-semibold tracking-wide"
            style={{
              color: '#00c7c4',
              fontSize: cinematic ? '0.8rem' : '0.65rem',
            }}
          >
            {currentStage.toUpperCase()} — {Math.round(progress)}%
          </span>
        )}
      </div>

      {/* SVG Diamond */}
      <div className="flex-1 min-h-0">
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Progress clip */}
            <clipPath id={clipId}>
              <rect
                x="0" y="0"
                width={clipWidth}
                height={VH}
                style={{ transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            </clipPath>
            {/* Glow filter for progress edge */}
            <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Base diamonds (unfilled) */}
          <polygon points={LEFT_DIAMOND}  fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
          <polygon points={RIGHT_DIAMOND} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />

          {/* Center divider */}
          <line x1={MID_X} y1={TOP_Y} x2={MID_X} y2={BOT_Y} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 3" />

          {/* Stage quarter lines */}
          {[Q1_X, Q3_X].map(x => (
            <line key={x} x1={x} y1={MID_Y - 28} x2={x} y2={MID_Y + 28}
              stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
          ))}

          {/* Filled diamonds (clipped by progress) */}
          <g clipPath={`url(#${clipId})`}>
            <polygon points={LEFT_DIAMOND}  fill="#00c7c4" opacity="0.75" />
            <polygon points={RIGHT_DIAMOND} fill="#00c7c4" opacity="0.75" />
          </g>

          {/* Progress edge glow */}
          {progress > 0 && progress < 100 && (
            <line
              x1={clipWidth} y1={0}
              x2={clipWidth} y2={VH}
              stroke="#00c7c4"
              strokeWidth="2"
              opacity="0.9"
              filter={`url(#${glowId})`}
              style={{ transition: 'x1 1.2s cubic-bezier(0.4,0,0.2,1), x2 1.2s cubic-bezier(0.4,0,0.2,1)' }}
            />
          )}

          {/* Stage labels */}
          {STAGES.map((stage, i) => (
            <text
              key={stage}
              x={STAGE_LABEL_X[i]}
              y={MID_Y + 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={currentStage === stage ? '#ffffff' : 'rgba(255,255,255,0.35)'}
              fontSize={cinematic ? 13 : 11}
              fontWeight={currentStage === stage ? '700' : '400'}
              letterSpacing="1.5"
              style={{ transition: 'fill 0.6s ease, font-weight 0.3s ease', textTransform: 'uppercase', userSelect: 'none' }}
            >
              {stage}
            </text>
          ))}

          {/* Diamond tip dots */}
          {[
            { cx: 0,     cy: MID_Y },
            { cx: MID_X, cy: MID_Y },
            { cx: VW,    cy: MID_Y },
          ].map(({ cx, cy }) => (
            <circle key={cx} cx={cx} cy={cy} r="4"
              fill={cx / VW * 100 <= progress ? '#00c7c4' : 'rgba(255,255,255,0.3)'}
              style={{ transition: 'fill 0.8s ease' }}
            />
          ))}
          {[Q1_X, Q3_X].map(cx => (
            <circle key={cx} cx={cx} cy={TOP_Y} r="3"
              fill={cx / VW * 100 <= progress ? '#00c7c4' : 'rgba(255,255,255,0.2)'}
              style={{ transition: 'fill 0.8s ease' }}
            />
          ))}
          {[Q1_X, Q3_X].map(cx => (
            <circle key={`b${cx}`} cx={cx} cy={BOT_Y} r="3"
              fill={cx / VW * 100 <= progress ? '#00c7c4' : 'rgba(255,255,255,0.2)'}
              style={{ transition: 'fill 0.8s ease' }}
            />
          ))}
        </svg>
      </div>

      {/* Current stage description */}
      {currentStage && (
        <div className="flex-shrink-0 text-center" style={{ fontSize: cinematic ? '0.75rem' : '0.6rem' }}>
          <span className="text-white/40 tracking-wide uppercase">
            {STAGE_DESCRIPTIONS[currentStage]}
          </span>
        </div>
      )}

      {/* Milestone callout */}
      {milestone && (
        <div
          className="flex-shrink-0 rounded px-3 py-2 border text-center"
          style={{
            backgroundColor: 'rgba(0, 199, 196, 0.1)',
            borderColor: 'rgba(0, 199, 196, 0.4)',
            fontSize: cinematic ? '0.8rem' : '0.65rem',
          }}
        >
          <div className="text-white/80 font-medium" style={{ letterSpacing: '0.02em' }}>
            {milestone}
          </div>
        </div>
      )}

      {/* Next step */}
      {nextStep && (
        <div
          className="flex-shrink-0 text-center"
          style={{ fontSize: cinematic ? '0.7rem' : '0.58rem' }}
        >
          <span className="text-white/35 italic">{nextStep}</span>
        </div>
      )}

      {/* Empty state */}
      {!dd && (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-white/25 text-xs tracking-wide uppercase">
            Assessment pending...
          </span>
        </div>
      )}
    </div>
  );
};

export default DoubleDiamondWidget;
